/*
 * Copyright 2026, OpenRemote Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import type { CSSResult, TemplateResult } from "lit";
import { OrElement } from "@openremote/or-element";
import { property } from "lit/decorators.js";
import type { WidgetConfig } from "./widget-config";
import type { WidgetSettings } from "./widget-settings";

export interface WidgetManifest {
  displayName: string;
  displayIcon: string;
  minColumnWidth?: number;
  minColumnHeight?: number;
  minPixelWidth?: number;
  minPixelHeight?: number;
  getContentHtml(config: WidgetConfig): OrWidget;
  getSettingsHtml(config: WidgetConfig): WidgetSettings;
  getDefaultConfig(): WidgetConfig;
}

// Main OrWidget class where all widgets extend their functionality on.
// It contains several methods used for rendering by the parent component; OrDashboardWidget
export abstract class OrWidget extends OrElement {
  protected static manifest: WidgetManifest;

  @property({ type: Object })
  protected readonly widgetConfig!: WidgetConfig;

  constructor(config: WidgetConfig) {
    super();
    this.widgetConfig = config;
  }

  static get styles(): CSSResult[] {
    return [];
  }

  /* --------------------------- */

  static getManifest(): WidgetManifest {
    if (!this.manifest) {
      throw new Error(`No manifest present on ${this.name}`);
    }
    return this.manifest;
  }

  // Method used for refreshing the content in a widget.
  // This can be customized to lower the performance- and/or visual impact of a refresh.
  public abstract refreshContent(force: boolean): void;

  // WebComponent lifecycle method to render HTML content
  protected abstract render(): TemplateResult;

  /* ------------------------------------- */

  public getDisplayName?: () => string | undefined;

  public getEditMode?: () => boolean;

  public getWidgetLocation?: () => { x?: number; y?: number; h?: number; w?: number };
}
